import type { GetServerSideProps } from 'next';
import { renderToString } from 'react-dom/server';
import { getServerState } from 'react-instantsearch-core';

import { parseUiStateFilters } from '@/utils/algolia';
import { getLocaleFromParams } from '@/utils/locale';
import { MATERIAL_EN_FILTERS } from '@/utils/straps';
import { extractPathFromUrl } from '@/utils/urlUtils';

import {
  applyProductConfigurationFilters,
  buildUiStateFromURL,
  getIndexName,
  ProductConfigurationFilterType,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import { PAGE_CONTEXT } from '@/integrations/algolia/types';
import {
  fetchCMSDefaultConfig,
  fetchCmsPageDataWithExtraData,
  fetchListingPageData,
} from '@/integrations/contentful';
import { getUserTokenForAlgoliaSSR } from '@/lib/algoliaUserToken';
import { commonServerSideProps } from '@/lib/commonPageStaticProps';
import { NEXT_PUBLIC_DEFAULT_APP_LOCALE } from '@/lib/const';
import { resolveContentDraftMode } from '@/lib/draftMode';
import { capture404Event } from '@/lib/errors';
import { PLP_ERRORS } from '@/lib/notFoundErrors';
import {
  getContentfulRegionFromLocale,
  getGqlRegionFromLocale,
  getRegionFromLocale,
} from '@/lib/regions';
import { CacheHeaderType, setCacheHeader } from '@/lib/revalidate';
import { StrapMaterialViewWrapper } from '@/views/StrapMaterialView/StrapMaterialViewWrapper';

import { DummyAllStrapsPage } from './all';

export const getServerSideProps: GetServerSideProps = commonServerSideProps(
  async (context) => {
    const { params, query, resolvedUrl } = context;
    const draftMode = resolveContentDraftMode(context);
    setCacheHeader(CacheHeaderType.NO_CACHE_TAG, context);
    try {
      const locale = getLocaleFromParams(params);
      const region = getRegionFromLocale(locale);
      const indexName = getIndexName(
        'PRODUCT',
        locale ?? NEXT_PUBLIC_DEFAULT_APP_LOCALE,
      );

      const initialUiState = buildUiStateFromURL(
        resolvedUrl,
        indexName,
        undefined,
        undefined,
        region.locale,
      );

      const material = Array.isArray(params?.material)
        ? (params?.material[0] ?? 'default')
        : params?.material || 'default';

      const { countryCode } = getContentfulRegionFromLocale(locale);
      const contentfulLocale = region.locale.contentful ?? region.locale.locale;

      const { cmsData: strapMaterialCmsData, seoConfigBuilder } =
        await fetchListingPageData(
          'strap material',
          contentfulLocale,
          null,
          null,
          material,
          countryCode,
          region.locale.code,
          draftMode,
        );

      if (!strapMaterialCmsData || strapMaterialCmsData.length === 0) {
        // handle some cms page but route is straps/xxxx like: /straps/change-watch-strap
        const { channel, languageCode } = getGqlRegionFromLocale(locale);
        const slugArr = ['straps', material];

        const slug = slugArr.join('/');

        const {
          breadcrumb,
          cmsData,
          cmsDefaultLayoutData,
          cmsExtraData,
          seoConfigBuilder,
        } = await fetchCmsPageDataWithExtraData(
          slug,
          contentfulLocale,
          languageCode,
          channel,
          countryCode,
          draftMode,
        );

        if (!cmsData || cmsData.length === 0) {
          return {
            notFound: true,
          };
        }
        return {
          props: {
            cmsData: cmsData,
            cmsExtraDataRecord: cmsExtraData,
            cmsDefaultLayoutData,
            breadcrumb,
            seoConfig: seoConfigBuilder?.build(),
          },
        };
      }
      const materialFilter =
        MATERIAL_EN_FILTERS[material] || MATERIAL_EN_FILTERS.default;

      const filterMaterial1 = materialFilter.filterMaterial1;
      const filterMaterial2 = materialFilter.filterMaterial2;

      const originalFilters = `(material_en:'${filterMaterial1}' OR material:'${filterMaterial1}')${
        filterMaterial2
          ? ` OR (material_en:'${filterMaterial2}' OR material:'${filterMaterial2}')`
          : ''
      }`;
      const strapFilters = `(strap_length.EN_GB:'Standard' OR strap_length.EN_US:'Standard' OR strap_length.EN:'Standard') AND (${originalFilters})`;

      const algoliaFilters = applyProductConfigurationFilters(
        ProductConfigurationFilterType.PLP,
        'Strap',
        draftMode,
        strapFilters,
      );

      const { cmsDefaultLayoutData, localeMessages } =
        await fetchCMSDefaultConfig(region.locale, region.country);

      const pageIndex = query?.page && parseInt(query.page as string);
      const filterAttributes = Object.entries(
        initialUiState[indexName]?.refinementList || {},
      )
        .filter(([, value]) => value && value.length > 0)
        .map(([key]) => key);

      // Convert URL query parameters to Algolia filters
      const urlFilters = parseUiStateFilters(initialUiState[indexName]);

      // Combine collection filters with URL filters
      const combinedFilters = urlFilters
        ? algoliaFilters
          ? `${algoliaFilters} AND ${urlFilters}`
          : urlFilters
        : algoliaFilters;

      // Perform initial search to get facets (without URL filters for translation)
      const algoliaSsrSearchClient = createSearchClient({
        isSsr: true,
        isPreview: draftMode,
        mockContext: PAGE_CONTEXT.PLP_STRAPS,
      });
      const index = algoliaSsrSearchClient.initIndex(indexName);
      const searchResponse = await index.search('', {
        filters: algoliaFilters,
        facets: ['*'],
        maxValuesPerFacet: 1000,
      });

      // Generate Algolia user token server-side for consistent SSR/client rendering
      const algoliaUserToken = getUserTokenForAlgoliaSSR(context.req) ?? null;

      const serverState = await getServerState(
        <DummyAllStrapsPage
          draftMode={draftMode}
          filterAttributes={filterAttributes}
          filters={combinedFilters}
          indexName={indexName}
          initialUiState={initialUiState}
          userToken={algoliaUserToken ?? undefined}
          {...(typeof pageIndex === 'number' ? { page: pageIndex - 1 } : {})}
        />,
        {
          renderToString,
        },
      );

      // Inject facets from the unfiltered search into serverState for translation
      // This ensures facets are available even when URL filters don't match any products
      if (serverState.initialResults?.[indexName]?.results?.[0]) {
        serverState.initialResults[indexName].results[0].facets =
          searchResponse.facets;
      }

      const nbPages = serverState.initialResults[indexName].results[0].nbPages;

      if (
        typeof pageIndex === 'number' &&
        (pageIndex > nbPages || pageIndex < 1)
      ) {
        capture404Event('Strap Material PLP', {
          requestUrl: context.req.url
            ? extractPathFromUrl(context.req.url)
            : '',
          reason: PLP_ERRORS.PLP_PAGINATION_OUT_OF_RANGE,
        });
        return {
          notFound: true,
        };
      }

      return {
        props: {
          isStrapMaterialView: true,
          algoliaFilters,
          algoliaUserToken,
          draftMode: draftMode ?? false,
          localeMessages,
          pageName: filterMaterial1 ?? '',
          instantSearchProps: {
            initialUiState,
            indexName,
            serverState,
          },
          cmsDefaultLayoutData: cmsDefaultLayoutData,
          cmsData: strapMaterialCmsData,
          seoConfig: seoConfigBuilder?.build(),
          serverState,
        },
      };
    } catch (error) {
      console.error('Failed to fetch server-side props', error);
      return { notFound: true };
    }
  },
);

export default StrapMaterialViewWrapper;
